/**
 *
 * Burgundy Engine Core
 *
 * Данная директория поставляется и обновляется скриптом обновления движка и
 * Изменения в ней вносятся только согласовано с разработчиком движка.
 * Иначе, любые изменения здесь могут быть перезаписаны автоматически!!!
 */

// Сброс стилей
import './starter/styles.css';

import { Reducer } from '@reduxjs/toolkit';
import { AppContent, RoutingMiniAppData, StarterFunctions } from './helpers/dna';
import {
    coreEventReducer,
    CoreZoomModalContent,
    ModalController,
    modalControllerReduces,
    profileTabsReducer,
    SHARED_MODALS,
} from './modules';
import { getAppNameFromAppString, getContextFromAppName, StarterSettings, authXSliceReducer } from '@brg/auth';
import { customThemeNameOrUndefined } from '../shared/helpers/customTheme';
import { whiteTheme } from '../shared/themes/whiteTheme';
import { greyTheme } from '../shared/themes/greyTheme';
import ReactDOM from 'react-dom';
import React from 'react';
import { BCoreProvider } from './providers';
import { BCoreStore } from './store';
import { ThemeProvider } from 'styled-components';
import { Deep } from '@brg/toolbox';
import { ApplicationTheme } from '../shared/themes';
import LangProvider from '../shared/components/__ADDONS__/__LANG__/Providers/LangProvider';
import { StarterUtils } from './starter';
import { ProfileTab } from './helpers/profileTabs';
import { BGID } from './auth';
import * as authenticationApp from 'core/apps/authentication/app';
import BurgundyEngineConfig from '../config';
import { BurgundyEngineDelegate } from '../delegate';
import AlteredHelmet from "../shared/components/AlteredHelmet/AlteredHelmet";
import { ErrorBoundaryInner } from 'shared/components/ErrorBoundary';
import { BrowserRouter } from 'react-router-dom';
import { CustomToastsProvider } from './providers/CustomToastsProviders';
import CookiesWarning from "../shared/components/CookiesWarning/CookiesWarning";
import ContextSwitch from "../shared/components/util/ContextSwitch";
import LegalsWarning from 'shared/components/LegalsWarning/LegalsWarning';
import { NotificationProvider } from "../shared/contexts/NotificationContext";
import MergeAccountsInfo from 'shared/components/MergeAccountsInfo/MergeAccountsInfo';
import { MainSearchController } from 'shared/components/MainSearchController/MainSearchController';
import { VoteProvider } from './providers/VoteProvider';
import { enginePaths } from '../shared/constants/enginePaths';
import TagCategoriesProvider from './providers/TagCategoriesProvider';
import ChannelsProvider from './providers/ChannelsProvider';

/**
 * Подключение утилит
 */
export * from './utils';

/**
 * Подключение модулей
 */
export * from './modules';

/**
 * Подключение хранилища ядра
 */
export * from './store';

/**
 * Подключение провайдеров ядра
 */
export * from './providers';

/**
 * Подключение роутов ядра
 */
export * from './routes';

/**
 * Подключение языковых модулей
 */
export * from './lang';

/**
 * Подключение библиотеки
 */
export * from './lib';

/**
 * Подключение хелперов
 */
export * from './helpers/layout';

/**
 * Подключение сервисов
 */
export { AuthXService, ProfileXService, BGID, AuthUtils, ProfileUtils } from '@brg/auth';

/**
 * Общие слайсы движка
 */
export const SHARED_REDUCERS: Record<string, Reducer> = {};

/**
 * Общие мини-приложения движка, доступные без регистрации
 */
export const SHARED_APPLICATIONS: Record<string, AppContent> = {};

/**
 * Функция настройки движка
 */
export const setupEngine = (): void => {
    // Настройка редуктора движка
    SHARED_REDUCERS['modalController'] = modalControllerReduces;
    SHARED_REDUCERS['profileTabs'] = profileTabsReducer;
    SHARED_REDUCERS['coreEvents'] = coreEventReducer;
    SHARED_REDUCERS['authX'] = authXSliceReducer;

    // Настройка базового приложения
    SHARED_APPLICATIONS[BurgundyEngineConfig.loginApplicationName] = authenticationApp.default;

    // Регистратор зум модального окна
    SHARED_MODALS['zoom'] = {
        solidBackground: true,
        closable: true,
        component: CoreZoomModalContent,
    };
};

interface ApplicationSharedI {
    application: string;
    profileTabs: Record<string, ProfileTab>;
}

export const ApplicationShared: ApplicationSharedI = {
    application: '',
    profileTabs: {},
};

/**
 * Стартер движка
 * @param miniApp
 * @param functions
 * @constructor
 */
export async function Starter(miniApp: RoutingMiniAppData, functions: StarterFunctions = {}): Promise<void> {
    try {
        // Настройка движка
        setupEngine();

        if (functions.beforeStart) {
            await functions.beforeStart();
        }

        let app = SHARED_APPLICATIONS[miniApp.application];
        console.log('Приложение успешно найдено', miniApp, SHARED_APPLICATIONS, app);
        if (!app) {

            //@ts-ignore
            const isExistedPath = enginePaths[miniApp.application];
            if (!isExistedPath && miniApp.basename) {
                const appArr = miniApp.application.split('/');
                app = (await import('../_' + appArr[0] + '/main' + '/' + StarterSettings.appStarterFile))?.default;
            } else {
                app = (await import('../_' + miniApp.application + '/' + StarterSettings.appStarterFile))?.default;
            }
        }


        document.title = app.manifest.title ?? StarterSettings.appDefaultTitle;
        ApplicationShared.application = miniApp.application;

        const customTheme = customThemeNameOrUndefined();
        let theme = app.manifest.theme ?? {};

        // @fixme ИСПРАВИТЬ ЭТО НЕДОРАЗУМЕНИЕ @Яков
        if (customTheme === 'white') {
            theme = whiteTheme
        }
        if (customTheme === 'grey') {
            theme = greyTheme
        }

        const applicationCallback = (bgid: string, fip: string) => {
            if (app.manifest.clear) {
                ReactDOM.render(
                    <React.StrictMode>
                        <app.App mode={miniApp.mode} />
                    </React.StrictMode>,
                    document.getElementById('root'),
                );
                return;
            }

            // Запуск рендера
            ReactDOM.render(
                <React.StrictMode>
                    <ContextSwitch />
                    {/* Подключение и настройка ядра приложения*/}
                    <BCoreProvider
                        bgid={bgid}
                        fip={fip}
                        projectName={ApplicationShared.application}
                        projectVersion={'-'}
                        store={BCoreStore({
                            ...app.manifest.reducer,
                            ...SHARED_REDUCERS,
                        })}
                        service={app.manifest.auth?.service ?? 'geography'}
                        applicationName={getAppNameFromAppString(miniApp.application)}
                        applicationContext={getContextFromAppName(miniApp.application)}
                    >
                        {/* Подключение темы */}
                        <ThemeProvider theme={Deep.merge(ApplicationTheme.grey, theme)}>
                            <CustomToastsProvider>
                                {/* Подключение языка */}
                                <LangProvider>
                                    {/* @ts-ignore */}
                                    <NotificationProvider>
                                        <VoteProvider>
                                            <TagCategoriesProvider>
                                                <ChannelsProvider>
                                                    <ErrorBoundaryInner>
                                                        {/* @ts-ignore */}
                                                        <BrowserRouter basename={miniApp.basename}>
                                                            <AlteredHelmet />
                                                            <app.App mode={miniApp.mode} />
                                                            <MainSearchController />
                                                            <ModalController />
                                                            <LegalsWarning />
                                                            <CookiesWarning />
                                                            <MergeAccountsInfo />
                                                        </BrowserRouter>
                                                    </ErrorBoundaryInner>
                                                </ChannelsProvider>
                                            </TagCategoriesProvider>
                                        </VoteProvider>
                                    </NotificationProvider>
                                </LangProvider>
                            </CustomToastsProvider>
                        </ThemeProvider>
                    </BCoreProvider>
                </React.StrictMode>,
                document.getElementById('root'),
            );
        };

        if (BurgundyEngineDelegate.shouldUseBGIDRequest(app.manifest)) {
            // BGID.work(app.manifest?.auth?.service ?? 'geography', ({ bgid, fip }) => {
            //     applicationCallback(bgid, fip);
            // });
        } else {
            applicationCallback('', '');
        }
    } catch (e) {
        StarterUtils.whiteAppNotFoundInEngine(miniApp.application);
        const storedApp = localStorage.getItem('current-app');
        if (storedApp) {
            localStorage.removeItem('current-app');
            window.location.reload()
        }
        console.warn(e);
    }
}
/**
 *
 * Burgundy Engine Core
 *
 * Данная директория поставляется и обновляется скриптом обновления движка и
 * Изменения в ней вносятся только согласовано с разработчиком движка.
 * Иначе, любые изменения здесь могут быть перезаписаны автоматически!!!
 */

// Сброс стилей
import './starter/styles.css';

import { Reducer } from '@reduxjs/toolkit';
import { AppContent, RoutingMiniAppData, StarterFunctions } from './helpers/dna';
import {
    coreEventReducer,
    CoreZoomModalContent,
    ModalController,
    modalControllerReduces,
    profileTabsReducer,
    SHARED_MODALS,
} from './modules';
import { getAppNameFromAppString, getContextFromAppName, StarterSettings, authXSliceReducer } from '@brg/auth';
import { customThemeNameOrUndefined } from '../shared/helpers/customTheme';
import { whiteTheme } from '../shared/themes/whiteTheme';
import { greyTheme } from '../shared/themes/greyTheme';
import ReactDOM from 'react-dom';
import React from 'react';
import { BCoreProvider } from './providers';
import { BCoreStore } from './store';
import { ThemeProvider } from 'styled-components';
import { Deep } from '@brg/toolbox';
import { ApplicationTheme } from '../shared/themes';
import LangProvider from '../shared/components/__ADDONS__/__LANG__/Providers/LangProvider';
import { StarterUtils } from './starter';
import { ProfileTab } from './helpers/profileTabs';
import { BGID } from './auth';
import * as authenticationApp from 'core/apps/authentication/app';
import BurgundyEngineConfig from '../config';
import { BurgundyEngineDelegate } from '../delegate';
import AlteredHelmet from "../shared/components/AlteredHelmet/AlteredHelmet";
import { ErrorBoundaryInner } from 'shared/components/ErrorBoundary';
import { BrowserRouter } from 'react-router-dom';
import { CustomToastsProvider } from './providers/CustomToastsProviders';
import CookiesWarning from "../shared/components/CookiesWarning/CookiesWarning";
import ContextSwitch from "../shared/components/util/ContextSwitch";
import LegalsWarning from 'shared/components/LegalsWarning/LegalsWarning';
import { NotificationProvider } from "../shared/contexts/NotificationContext";
import MergeAccountsInfo from 'shared/components/MergeAccountsInfo/MergeAccountsInfo';
import { MainSearchController } from 'shared/components/MainSearchController/MainSearchController';
import { VoteProvider } from './providers/VoteProvider';
import { enginePaths } from '../shared/constants/enginePaths';
import TagCategoriesProvider from './providers/TagCategoriesProvider';
import ChannelsProvider from './providers/ChannelsProvider';

/**
 * Подключение утилит
 */
export * from './utils';

/**
 * Подключение модулей
 */
export * from './modules';

/**
 * Подключение хранилища ядра
 */
export * from './store';

/**
 * Подключение провайдеров ядра
 */
export * from './providers';

/**
 * Подключение роутов ядра
 */
export * from './routes';

/**
 * Подключение языковых модулей
 */
export * from './lang';

/**
 * Подключение библиотеки
 */
export * from './lib';

/**
 * Подключение хелперов
 */
export * from './helpers/layout';

/**
 * Подключение сервисов
 */
export { AuthXService, ProfileXService, BGID, AuthUtils, ProfileUtils } from '@brg/auth';

/**
 * Общие слайсы движка
 */
export const SHARED_REDUCERS: Record<string, Reducer> = {};

/**
 * Общие мини-приложения движка, доступные без регистрации
 */
export const SHARED_APPLICATIONS: Record<string, AppContent> = {};

/**
 * Функция настройки движка
 */
export const setupEngine = (): void => {
    // Настройка редуктора движка
    SHARED_REDUCERS['modalController'] = modalControllerReduces;
    SHARED_REDUCERS['profileTabs'] = profileTabsReducer;
    SHARED_REDUCERS['coreEvents'] = coreEventReducer;
    SHARED_REDUCERS['authX'] = authXSliceReducer;

    // Настройка базового приложения
    SHARED_APPLICATIONS[BurgundyEngineConfig.loginApplicationName] = authenticationApp.default;

    // Регистратор зум модального окна
    SHARED_MODALS['zoom'] = {
        solidBackground: true,
        closable: true,
        component: CoreZoomModalContent,
    };
};

interface ApplicationSharedI {
    application: string;
    profileTabs: Record<string, ProfileTab>;
}

export const ApplicationShared: ApplicationSharedI = {
    application: '',
    profileTabs: {},
};

/**
 * Стартер движка
 * @param miniApp
 * @param functions
 * @constructor
 */
export async function Starter(miniApp: RoutingMiniAppData, functions: StarterFunctions = {}): Promise<void> {
    try {
        // Настройка движка
        setupEngine();

        if (functions.beforeStart) {
            await functions.beforeStart();
        }

        let app = SHARED_APPLICATIONS[miniApp.application];
        console.log('Приложение успешно найдено', miniApp, SHARED_APPLICATIONS, app);
        if (!app) {

            //@ts-ignore
            const isExistedPath = enginePaths[miniApp.application];
            if (!isExistedPath && miniApp.basename) {
                const appArr = miniApp.application.split('/');
                app = (await import('../_' + appArr[0] + '/main' + '/' + StarterSettings.appStarterFile))?.default;
            } else {
                app = (await import('../_' + miniApp.application + '/' + StarterSettings.appStarterFile))?.default;
            }
        }


        document.title = app.manifest.title ?? StarterSettings.appDefaultTitle;
        ApplicationShared.application = miniApp.application;

        const customTheme = customThemeNameOrUndefined();
        let theme = app.manifest.theme ?? {};

        // @fixme ИСПРАВИТЬ ЭТО НЕДОРАЗУМЕНИЕ @Яков
        if (customTheme === 'white') {
            theme = whiteTheme
        }
        if (customTheme === 'grey') {
            theme = greyTheme
        }

        const applicationCallback = (bgid: string, fip: string) => {
            if (app.manifest.clear) {
                ReactDOM.render(
                    <React.StrictMode>
                        <app.App mode={miniApp.mode} />
                    </React.StrictMode>,
                    document.getElementById('root'),
                );
                return;
            }

            // Запуск рендера
            ReactDOM.render(
                <React.StrictMode>
                    <ContextSwitch />
                    {/* Подключение и настройка ядра приложения*/}
                    <BCoreProvider
                        bgid={bgid}
                        fip={fip}
                        projectName={ApplicationShared.application}
                        projectVersion={'-'}
                        store={BCoreStore({
                            ...app.manifest.reducer,
                            ...SHARED_REDUCERS,
                        })}
                        service={app.manifest.auth?.service ?? 'geography'}
                        applicationName={getAppNameFromAppString(miniApp.application)}
                        applicationContext={getContextFromAppName(miniApp.application)}
                    >
                        {/* Подключение темы */}
                        <ThemeProvider theme={Deep.merge(ApplicationTheme.grey, theme)}>
                            <CustomToastsProvider>
                                {/* Подключение языка */}
                                <LangProvider>
                                    {/* @ts-ignore */}
                                    <NotificationProvider>
                                        <VoteProvider>
                                            <TagCategoriesProvider>
                                                <ChannelsProvider>
                                                    <ErrorBoundaryInner>
                                                        {/* @ts-ignore */}
                                                        <BrowserRouter basename={miniApp.basename}>
                                                            <AlteredHelmet />
                                                            <app.App mode={miniApp.mode} />
                                                            <MainSearchController />
                                                            <ModalController />
                                                            <LegalsWarning />
                                                            <CookiesWarning />
                                                            <MergeAccountsInfo />
                                                        </BrowserRouter>
                                                    </ErrorBoundaryInner>
                                                </ChannelsProvider>
                                            </TagCategoriesProvider>
                                        </VoteProvider>
                                    </NotificationProvider>
                                </LangProvider>
                            </CustomToastsProvider>
                        </ThemeProvider>
                    </BCoreProvider>
                </React.StrictMode>,
                document.getElementById('root'),
            );
        };

        if (BurgundyEngineDelegate.shouldUseBGIDRequest(app.manifest)) {
            // BGID.work(app.manifest?.auth?.service ?? 'geography', ({ bgid, fip }) => {
            //     applicationCallback(bgid, fip);
            // });
        } else {
            applicationCallback('', '');
        }
    } catch (e) {
        StarterUtils.whiteAppNotFoundInEngine(miniApp.application);
        const storedApp = localStorage.getItem('current-app');
        if (storedApp) {
            localStorage.removeItem('current-app');
            window.location.reload()
        }
        console.warn(e);
    }
}
